import * as React from "react";
import { Icon } from "../actions/Icon";

/**
 * Compact exclusive choice. Used for per-meeting capture (Bot / Desktop / Off), notes tabs on mobile, recorder camera/audio.
 */
export interface SegmentedControlProps {
  options: (string | { value: string; label: React.ReactNode; icon?: string })[];
  value?: string;
  defaultValue?: string;
  onChange?: (value: string) => void;
  size?: "md" | "lg";
  /** default · pill · media (on top of video) */
  variant?: "default" | "pill" | "media";
  block?: boolean;
  /** Accessible name for the group, e.g. "Filter recordings". */
  label?: string;
  className?: string;
}

export function SegmentedControl({
  options = [],
  value,
  defaultValue,
  onChange,
  size = "md",
  variant = "default",
  block,
  label,
  className,
}: SegmentedControlProps): JSX.Element {
  const opts = options.map((o) => (typeof o === "string" ? { value: o, label: o as React.ReactNode, icon: undefined } : o));
  const [inner, setInner] = React.useState<string | undefined>(
    defaultValue !== undefined ? defaultValue : opts[0] && opts[0].value,
  );
  const cur = value === undefined ? inner : value;
  const cls = [
    "gk-seg",
    size === "lg" && "gk-seg--lg",
    variant !== "default" && "gk-seg--" + variant,
    block && "gk-seg--block",
    className,
  ]
    .filter(Boolean)
    .join(" ");
  return (
    <div className={cls} role="group" aria-label={label}>
      {opts.map((o) => (
        <button
          key={o.value}
          type="button"
          className="gk-seg__item"
          aria-pressed={cur === o.value}
          onClick={() => {
            if (value === undefined) setInner(o.value);
            onChange?.(o.value);
          }}
        >
          {o.icon && <Icon name={o.icon} size={16} />}
          {o.label}
        </button>
      ))}
    </div>
  );
}
